import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { cva } from 'class-variance-authority';
import {
  Children,
  createContext,
  CSSProperties,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

export interface TourProps {
  dataAttr?: string;
  children: ReactElement<TourStepProps>[];
  onEnd?: () => void;
  visible?: boolean;
  padding?: {
    x: number;
    y: number;
  };
  render?: (node: ReactNode, bounds: Bounds | null) => ReactElement;
}

interface TourContext {
  step: number;
  nextStep: () => void;
  prevStep: () => void;
}

const TourContext = createContext({} as TourContext);

const useTourContext = () => useContext(TourContext);

export function useTour(localStorageKey: string = 'tour-shown') {
  const [tourShown, _setTourShown] = useState(
    localStorage.getItem(localStorageKey) === 'true'
  );
  const setTourShown = (shown: boolean) => {
    _setTourShown(shown);
    localStorage.setItem(localStorageKey, 'true');
  };
  return {
    tourShown,
    setTourShown,
    clearTourShown: () => {
      localStorage.removeItem(localStorageKey);
    }
  };
}

type Bounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export function Tour({
  dataAttr = 'tour',
  children,
  onEnd,
  padding = {
    x: 0,
    y: 0
  },
  visible = true
}: TourProps) {
  const [step, setStep] = useState(0);
  const nextStep = useCallback(() => {
    setStep((prev) => prev + 1);
  }, [setStep]);
  const prevStep = useCallback(() => {
    setStep((prev) => {
      if (prev === 0) {
        return 0;
      }
      return prev - 1;
    });
  }, [setStep]);

  const steps = Children.map(children, (child) => {
    if ('name' in child.props) {
      return child.props.name;
    }
    return null;
  }).filter(Boolean);

  const stepNodes = Children.map(children, (child) => {
    if ('name' in child.props) {
      return child;
    }
    return null;
  }).filter(Boolean);

  const stepCount = stepNodes.length;
  const stepDone = step >= stepCount;

  useEffect(() => {
    if (stepDone) {
      onEnd?.();
    }
  }, [step, children, onEnd, stepDone]);

  useEffect(() => {
    const fn = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === 'Enter') {
        nextStep();
      }
    };

    window.addEventListener('keydown', fn);
    return () => {
      window.removeEventListener('keydown', fn);
    };
  }, [step, children, onEnd, stepDone]);

  if (!visible) {
    return null;
  }

  const count = steps.length;
  const done = step === count;
  if (done) {
    return null;
  }
  return (
    <TourContext.Provider value={{ step, nextStep, prevStep }}>
      {/* {
        !introDone ? introNodes[intro] : null
      } */}
      <>
        {/* {
          <div
            className="fixed w-full h-full bg-neutral-600 opacity-50 top-0 left-0 animate-none z-50"
            style={position && !stepNodes[step]?.props.noHighlight ? {
              ...clipPathStyle
            } : {}}
          ></div>
        } */}
        {stepNodes[step]}
        {/* {hasRender(stepNodes[step]) ?
          stepNodes[step].props.render?.(stepNodes[step], position ?? null, nextStep) :
          <DefaultRender node={stepNodes[step]} bounds={position ?? null} />
        } */}
      </>
    </TourContext.Provider>
  );
}

export function FancyRender(props: {
  node: ReactNode;
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  const strokeWidth = 4;
  return (
    <div
      className="fixed z-50 "
      style={{
        top: `${props.y}px`,
        left: `${props.x}px`,
        width: `${props.width}px`,
        height: `${props.height}px`
      }}
    >
      <div className="left-full absolute">{props.node}</div>
      <div
        style={{
          clipPath: `polygon(
            0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
            ${strokeWidth}px ${strokeWidth}px,
            ${strokeWidth}px calc(100% - ${strokeWidth}px),
            calc(100% - ${strokeWidth}px) calc(100% - ${strokeWidth}px),
            calc(100% - ${strokeWidth}px) ${strokeWidth}px,
            ${strokeWidth}px ${strokeWidth}px
          )`
        }}
        className="w-full h-full border-orange-2 border-2 animate-border dark:text-neutral-200 animate-[pulse_1s_ease-in-out_infinite]"
      ></div>
    </div>
  );
}

const DefaultRenderTipStyles = cva(['absolute'], {
  variants: {
    side: {
      left: ['right-0', ''],
      right: ['left-full']
    }
  }
});

function DefaultOutline(props: { node: ReactNode; bounds: Bounds | null }) {
  return (
    <div
      className="fixed z-50"
      style={{
        top: `${props.bounds?.y}px`,
        left: `${props.bounds?.x}px`,
        width: `${props.bounds?.width}px`,
        height: `${props.bounds?.height}px`
      }}
    >
      <div className="w-full h-full border-orange-2 border-2 dark:text-neutral-200 animate-[pulse_1s_ease-in-out_infinite]"></div>
    </div>
  );
}

const TourStepStyles = cva(
  [
    'fixed flex items-center gap-2 bg-white text-neutral-800 opacity-100 animate-none p-2 z-50 whitespace-nowrap w-fit'
  ],
  {
    variants: {
      side: {
        left: ['right-0'],
        right: ['translate-x-0']
      }
    }
  }
);

type TourStepProps = TourStepRenderProps | TourStepChildrenProps;

type TourStepRenderProps = {
  name: string;
  render: (
    el: Element | null,
    bounds: Bounds | null,
    next: () => void,
    prev: () => void
  ) => ReactElement;
  children?: never;
  background?: 'none' | 'highlight' | 'shade';
};

type TourStepChildrenProps = {
  children: ReactNode;
  padding?: {
    x: number;
    y: number;
  };
  side?: 'left' | 'right';
  name: string;
  render?: never;
  background?: 'none' | 'highlight' | 'shade';
};

export function TourStep(props: TourStepProps) {
  const { nextStep, prevStep } = useTourContext();
  const side = hasSideProp(props) ? props.side : 'right';
  const isRender = hasRender(props);

  const [position, setPosition] = useState<Bounds | null>();

  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (retry > 10) {
      return;
    }
    setTimeout(() => {
      const selector = `[data-tour="${props.name}"]`;
      const c = document.querySelector(selector);
      if (c) {
        setEl(c);
        const bounds = c.getBoundingClientRect();
        setRetry(0);
        setPosition({
          x: bounds.left,
          y: bounds.top,
          width: c.clientWidth,
          height: c.clientHeight
        });
      } else {
        setRetry(retry + 1);
      }
    }, 100);
  }, [setPosition, props.name, retry, setRetry]);

  const [el, setEl] = useState<Element | null>(null);
  const clipPathStyle = position
    ? {
        clipPath: `polygon(
          0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
          ${position.x}px ${position.y}px,
          ${position.x}px ${position.y + position.height}px,
          ${position.x + position.width}px ${position.y + position.height}px,
          ${position.x + position.width}px ${position.y}px,
          ${position.x}px ${position.y}px
        )`
      }
    : {};

  const background = props.background ?? 'highlight';
  if (isRender) {
    return (
      <>
        {props.render(el, position ?? null, nextStep, prevStep)}
        {position && background !== 'none' && (
          <div
            className="fixed w-full h-full bg-neutral-600 opacity-50 top-0 left-0 animate-none z-40 pointer-events-none"
            style={
              position && background === 'highlight'
                ? {
                    ...clipPathStyle
                  }
                : {}
            }
          />
        )}
      </>
    );
  }

  let positionStyle: CSSProperties = {};
  if (position) {
    if (side === 'left') {
      positionStyle.left = `${position.x - (props.padding?.x ?? 0)}px`;
      positionStyle.transform = `translate(-100%, -50%)`;
    } else {
      positionStyle.left = `${
        position.x + position.width + (props.padding?.x ?? 0)
      }px`;
      positionStyle.transform = `translateY(-50%)`;
    }
    positionStyle.top = `${position.y + position.height / 2}px`;
  }
  return (
    <>
      {
        <div
          className="fixed w-full h-full bg-neutral-600 opacity-50 top-0 left-0 animate-none z-40 pointer-events-none"
          style={
            position && background === 'highlight'
              ? {
                  ...clipPathStyle
                }
              : {}
          }
        />
      }
      {position && (
        <DefaultOutline
          node={props.children}
          bounds={{
            x: position.x - (props.padding?.x ?? 0),
            y: position.y - (props.padding?.y ?? 0),
            width: position.width + (props.padding?.x ?? 0) * 2,
            height: position.height + (props.padding?.y ?? 0) * 2
          }}
        />
      )}
      {position && (
        <div
          style={{
            ...positionStyle
          }}
          className={TourStepStyles({ side })}
        >
          {props.children}
          <button
            className="flex justify-end p-1 rounded-sm border"
            onClick={() => {
              prevStep();
            }}
          >
            <ArrowLeft />
          </button>
          <button
            className="flex justify-end p-1 rounded-sm bg-orange-2 text-white"
            onClick={() => {
              nextStep();
            }}
          >
            <ArrowRight />
          </button>
        </div>
      )}
    </>
  );
}

function hasRender(props: unknown): props is TourStepRenderProps {
  if (!props) return false;
  return 'render' in (props as TourStepProps);
}

function hasSideProp(props: unknown): props is TourStepChildrenProps {
  if (!props) return false;
  return 'side' in (props as TourStepProps);
}
